import { useEffect, useRef } from 'react';
import {
  INITIAL_STATE as INITIAL_BOTTOM_TAB,
  Tab,
} from '@paradigm/features/src/unified-rfqs/ui/bottom-tabs';
import { PinMode } from '#/features/terminal/drawer-context';

interface Params {
  readonly pinMode: PinMode;
  readonly isOpen: boolean;
  readonly tab: Tab;
  readonly isBlockTerminalEnabled: boolean;
  readonly setTab: (tab: Tab) => void;
}

/**
 * Keeps the active bottom tab in step with the terminal drawer's pin mode:
 *
 *   - Entering side-panel mode while `block-terminal` is the active bottom
 *     tab parks the strip on `INITIAL_BOTTOM_TAB` so the slot hides without
 *     leaving an empty pane.
 *   - Flipping side-panel -> bottom-tab while the drawer is open focuses
 *     `block-terminal` so the terminal stays visible where it now lives.
 */
export default function useSyncBottomTabWithPinMode({
  pinMode,
  isOpen,
  tab,
  isBlockTerminalEnabled,
  setTab,
}: Params) {
  const prevPinModeRef = useRef(pinMode);
  const sidePanelOpen = pinMode === 'side-panel' && isOpen;

  useEffect(() => {
    const prevPinMode = prevPinModeRef.current;
    prevPinModeRef.current = pinMode;

    if (sidePanelOpen && tab === 'block-terminal') {
      setTab(INITIAL_BOTTOM_TAB);
      return;
    }

    if (
      prevPinMode === 'side-panel' &&
      pinMode === 'bottom-tab' &&
      isOpen &&
      isBlockTerminalEnabled
    ) {
      setTab('block-terminal');
    }
  }, [sidePanelOpen, tab, setTab, pinMode, isOpen, isBlockTerminalEnabled]);
}
